import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import MembraneField, { PRESETS } from './MembraneField';
import { useField } from '../lib/field';

// Each section sits at its own spot in the field, in nav order, so moving
// between them pans across it more slowly than the pages swipe.
const SPOTS = ['/', '/projects', '/media', '/blog', '/about'];
const spotFor = (pathname) => Math.max(0, SPOTS.indexOf(`/${pathname.split('/')[1] || ''}`));

// The ghosts field behind the whole site. It's mounted once and never leaves:
// pages slide over it (the page transition leaves it out of the push), and
// scrolling drifts it up and closer, slower than the page, so moving through
// the site reads as moving through the field.
const Backdrop = () => {
  const reduce = useReducedMotion();
  const { pathname } = useLocation();
  const { look, preset, light, setEditing } = useField();
  const ref = useRef(null);

  // The edit panel belongs to the page it was opened on.
  useEffect(() => setEditing(false), [pathname, setEditing]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    let frame = 0;
    // How far down, 0 to 2. It follows the scroll a screen at a time, but
    // slows on a long page so it only reaches 2 at the very bottom: the field
    // keeps moving for as long as the page does, and never stops short.
    const apply = () => {
      frame = 0;
      const screen = Math.max(window.innerHeight, 1);
      const room = Math.max(document.documentElement.scrollHeight - screen, 1);
      const depth = Math.min(window.scrollY / screen, (2 * window.scrollY) / room, 2);
      node.style.setProperty('--depth', reduce ? '0' : depth.toFixed(4));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reduce, pathname]);

  return (
    <div
      ref={ref}
      className="backdrop"
      style={{ '--spot': reduce ? 0 : spotFor(pathname) }}
      aria-hidden="true"
    >
      <div className="backdrop-pan">
        <div className="backdrop-drift">
          <MembraneField
            reduce={Boolean(reduce)}
            look={look}
            scene={PRESETS[preset].id}
            onReady={light}
            className="backdrop-field"
          />
        </div>
      </div>
    </div>
  );
};

export default Backdrop;
